"use client";

import * as React from "react";
import { cn } from "../../lib/utils";

export interface ChipTabOption {
  key: string;
  label: string;
}

export interface ChipTabsProps {
  options: ChipTabOption[];
  selected: string;
  onSelect: (key: string) => void;
  sticky?: boolean;
  trailingContent?: React.ReactNode;
  className?: string;
}

function getChipButtonClasses(isActive: boolean) {
  return cn(
    "inline-flex min-h-8 shrink-0 items-center rounded-lg border px-3 text-xs font-medium transition-colors",
    isActive
      ? "border-[#0D41E1] bg-white text-[#0D41E1]"
      : "border-[#D1D1D6] bg-white text-[#667085] hover:bg-[#F8FAFC]"
  );
}

/**
 * Renders a horizontally scrollable row of chips with optional sticky-on-scroll behavior.
 */
export function ChipTabs({
  options,
  selected,
  onSelect,
  sticky,
  trailingContent,
  className,
}: ChipTabsProps) {
  const [isStuck, setIsStuck] = React.useState(false);
  const observerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!sticky) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsStuck(entry.intersectionRatio < 1);
      },
      { threshold: [1], rootMargin: "-1px 0px 0px 0px" }
    );

    const target = observerRef.current;
    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) observer.unobserve(target);
    };
  }, [sticky]);

  return (
    <>
      {sticky && (
        <div
          ref={observerRef}
          className="absolute -top-px left-0 h-px w-full pointer-events-none"
          aria-hidden="true"
        />
      )}
      <section
        className={cn(
          "px-0 py-0 transition-shadow duration-200",
          sticky && "sticky top-0 z-20 -mx-4 bg-[#FFFAEF] px-4",
          isStuck && sticky && "shadow-[0_2px_10px_rgba(0,0,0,0.05)] border-b border-[#EAECF0]",
          className
        )}
      >
        <div
          className="flex gap-2 overflow-x-auto whitespace-nowrap py-2 [&::-webkit-scrollbar]:hidden"
          style={{ msOverflowStyle: "none", scrollbarWidth: "none" }}
        >
          {options.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => onSelect(option.key)}
              className={getChipButtonClasses(selected === option.key)}
            >
              {option.label}
            </button>
          ))}
          {trailingContent}
        </div>
      </section>
    </>
  );
}
